import React, { memo, useState, useRef } from 'react';
import { clsx } from 'js/utils/classNames.util';
import { useComponentTranslations } from 'js/hooks/component';
import { Checkbox } from 'js/components/common/Fields/Checkbox';
import { TFormFieldContactPreferences } from 'js/components/common/Form/types';

import * as hooks from './hooks';
import { Preference } from './components/Preference';
import { useFieldLabel } from '../ChangeFormField/hooks';
import { CONTACT_PREFERENCES_FIELD_TYPE, SELECT_ALL_SUFFIX } from '../../constants';

import style from './ContactPreferences.scss';

export const ContactPreferencesBare: React.FC<{
    className?: string;
    field: TFormFieldContactPreferences;
    fieldId: string;
}> = ({ className, field, fieldId }) => {
    const translate = useComponentTranslations();
    const [fieldLabel, getLabelText] = useFieldLabel(field.label);
    const [refsList, setRefsList] = useState<React.RefObject<HTMLInputElement>[]>([]);
    const selectAllFieldRef = useRef<HTMLInputElement>(null);
    const handleSelectAllItemsChange = hooks.useHandleSelectAllChange({ selectAllFieldRef, refsList });

    switch (field.preferencesType) {
        case CONTACT_PREFERENCES_FIELD_TYPE.PROMOTIONAL:
        case CONTACT_PREFERENCES_FIELD_TYPE.NON_PROMOTIONAL: {
            const { contactPreferences = [], enableSelectAll = false } = field;
            const channels = contactPreferences.map(({ channel }) => channel);
            const isProductsAvailable = contactPreferences.some(pref => pref?.products?.length);

            return (
                <div className={clsx(
                    [className, style.container, 'cms-contact-preferences', `cms-contact-preferences-${field.preferencesType}`]
                )}>
                    {isProductsAvailable && (
                        <>
                            {enableSelectAll && (
                                <div className={style.singleCheckboxContainer} key={`${field.name}${SELECT_ALL_SUFFIX}`}>
                                    <Checkbox
                                        aria-label={translate('playtech.mobile.system.form.contactPreferences.selectAll.label')}
                                        id={`${fieldId}-${field.name}${SELECT_ALL_SUFFIX}`}
                                        itemRef={selectAllFieldRef}
                                        label={translate('playtech.mobile.system.form.contactPreferences.selectAll.label')}
                                        name={`${field.name}${SELECT_ALL_SUFFIX}`}
                                        onChange={handleSelectAllItemsChange}
                                    />
                                </div>
                            )}
                            {contactPreferences.map(pref => (
                                <Preference
                                    channel={pref.channel}
                                    field={field}
                                    fieldId={fieldId}
                                    isProductsAvailable={isProductsAvailable}
                                    key={`${field.name}-${pref.channel}`}
                                    mainSelectAllFieldRef={selectAllFieldRef}
                                    options={pref.products}
                                    setRefsList={setRefsList}
                                />
                            ))}
                        </>
                    )}
                    {!isProductsAvailable && (
                        <Preference
                            field={field}
                            fieldId={fieldId}
                            options={channels}
                        />
                    )}
                </div>
            );
        }
        case CONTACT_PREFERENCES_FIELD_TYPE.GENERAL:
        case CONTACT_PREFERENCES_FIELD_TYPE.THIRD_PARTY: {
            return (
                <Checkbox
                    aria-label={getLabelText() || translate('playtech.mobile.system.checkbox.aria-label')}
                    className={clsx(
                        [className, 'cms-contact-preferences', `cms-contact-preferences-${field.preferencesType}`]
                    )}
                    id={`${fieldId}-${field.name}`}
                    label={fieldLabel}
                    name={field.name}
                />
            );
        }

        default: return null;
    }
};

export const ContactPreferences = memo(ContactPreferencesBare);
